/**
 * useErrorNavigation — Navigate to a specific error or input model data.
 * Phase 3B: uses queryClient.setQueryData for tree cache updates
 * instead of direct state mutation + reloadState.
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiService from '../../../services/ApiService';
import { useEditorStore } from '../../../shared/stores/useEditorStore';
import { ModelSection, ModelSectionType } from '../../../types/ModelSection';
import { typeMatchesSection } from '../../../utils/modelDataMapper';
import { mapModelData } from '../services/dataMappers';
import { SCENARIO_TREE_KEY, type EditorState } from '../../../shared/hooks/queries/useScenarioTree';

interface UseErrorNavigationParams {
    scenarioId: number;
    sections: ModelSection[];
    currentScenario: any;
    currentSensitivity: any;
    setCurrentSensitivity: (s: any) => void;
    setCurrentSection: (s: ModelSection | null) => void;
    setViewMode: (v: 'overview' | 'section') => void;
    setModelDataSelected: (item: any) => void;
    location: any;
}

/**
 * Resolve the best section to navigate to given an error's ancestor path.
 *
 * Sections 1-5 (Company, Asset, Equity, Corporate Taxation, Bank Accounts) all
 * share `company` as their root type, which means the bare `company` ancestor
 * matches every one of them via `typeMatchesSection`. To pick the right one we
 * walk the path from leaf to root: the first ancestor whose type is claimed by
 * exactly one non-Company section wins. If every ancestor matches multiple
 * sections (i.e. the error lives at the company level itself), we fall back to
 * Company — whose Properties panel is where company-level fields are edited.
 */
function resolveErrorSection(
    path: Array<{ type: string }>,
    sections: ModelSection[],
): ModelSection | null {
    let companyFallback: ModelSection | null = null;

    for (let i = path.length - 1; i >= 0; i--) {
        const ancestorType = path[i].type;
        const matches = sections.filter(s => typeMatchesSection(ancestorType, s.config.id));
        if (matches.length === 0) continue;

        const specific = matches.filter(s => s.config.id !== ModelSectionType.COMPANY);
        if (specific.length === 1) {
            return specific[0];
        }

        if (!companyFallback) {
            companyFallback = matches.find(s => s.config.id === ModelSectionType.COMPANY) ?? null;
        }
    }

    return companyFallback;
}

export function useErrorNavigation({
    scenarioId,
    sections,
    currentScenario,
    currentSensitivity,
    setCurrentSensitivity,
    setCurrentSection,
    setViewMode,
    setModelDataSelected,
    location,
}: UseErrorNavigationParams) {
    const queryClient = useQueryClient();

    const navigateToError = useCallback(async (error: any) => {
        // If the error is from the main scenario and we're viewing a sensitivity,
        // switch back to the main scenario so the user can see/fix the error.
        if (error.source === 'scenario' && currentSensitivity && currentSensitivity !== 'null') {
            setCurrentSensitivity('null');
        }
        const itemId = error.itemId?.toString();
        if (!itemId) return;
        const path = error._path || [];
        const targetSection = resolveErrorSection(path, sections);
        if (!targetSection) return;

        setCurrentSection(targetSection);
        setViewMode('section');
        const loadedItems: Record<string, any> = {};
        try {
            const response = await apiService.getSectionData(currentScenario.id, targetSection.config.id, false);
            if (response?.ok) {
                const responseData = await response.json();
                for (const md of (responseData['input'] || [])) {
                    loadedItems[md.id.toString()] = mapModelData(md);
                }
            }
        } catch (err) { return; }

        for (const ancestor of path) {
            const ancestorId = ancestor.id.toString();
            const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
            const ancestorItem = loadedItems[ancestorId] || currentState?.Input?.tree?.items?.[ancestorId];
            if (!ancestorItem) continue;
            useEditorStore.getState().setNodeExpanded('Input' + ancestorId, true);
            if (loadedItems[ancestorId]) loadedItems[ancestorId].expanded = true;
            if (ancestorItem.childrens?.length > 0) {
                const firstChildId = ancestorItem.childrens[0];
                const currentItems = currentState?.Input?.tree?.items ?? {};
                if (!loadedItems[firstChildId] && !currentItems[firstChildId]) {
                    try {
                        const resp = await apiService.getModelDataChildren(parseInt(ancestorId));
                        if (resp?.ok) { for (const child of await resp.json()) { loadedItems[child.id.toString()] = mapModelData(child); } }
                    } catch (err) { /* ignore */ }
                }
            }
        }

        queryClient.setQueryData<EditorState>(
            [SCENARIO_TREE_KEY, scenarioId],
            (old) => {
                if (!old) return old;
                const newInputItems = { ...old.Input.tree.items, ...loadedItems };
                for (const ancestor of path) {
                    const id = ancestor.id.toString();
                    if (newInputItems[id]) newInputItems[id] = { ...newInputItems[id], expanded: true };
                }
                const realRootId = Object.keys(loadedItems).find(id => loadedItems[id]?.type === 'input-root') || old.Input.tree.rootId;
                return {
                    ...old,
                    Input: {
                        ...old.Input,
                        tree: { ...old.Input.tree, items: newInputItems, rootId: realRootId },
                    },
                };
            }
        );

        setTimeout(() => {
            const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
            const targetItem = currentState?.Input?.tree?.items?.[itemId] || loadedItems[itemId];
            if (targetItem) setModelDataSelected(targetItem);
            setTimeout(() => {
                const el = document.querySelector(`[data-item-id="${itemId}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200);
        }, 100);
    }, [queryClient, scenarioId, sections, currentScenario, currentSensitivity, setCurrentSensitivity, setCurrentSection, setViewMode, setModelDataSelected]);

    // Navigate to target input model data (from Calculation Results)
    useEffect(() => {
        const targetId = location.state?.targetInputModelDataId;
        if (!targetId) return;
        const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
        if (!currentState?.Input?.tree?.items) return;
        const inputTree = currentState.Input.tree;
        const item = inputTree.items[targetId];
        if (!item) return;

        let current = item;
        while (current.parentId) {
            current = inputTree.items[current.parentId];
            if (current) { useEditorStore.getState().setNodeExpanded('Input' + current.id, true); }
        }
        if (item.childrens?.length > 0) { useEditorStore.getState().setNodeExpanded('Input' + item.id, true); }

        const ancestorIds = new Set<number>();
        current = item;
        while (current) { ancestorIds.add(parseInt(current.id)); current = current.parentId ? inputTree.items[current.parentId] : null; }
        const targetSection = sections.find(section => section.inputItems.some(si => ancestorIds.has(si.id)));
        if (targetSection) { setCurrentSection(targetSection); setViewMode('section'); }
        setModelDataSelected(item);

        // Expand nodes in the cache
        queryClient.setQueryData<EditorState>(
            [SCENARIO_TREE_KEY, scenarioId],
            (old) => {
                if (!old) return old;
                const newItems = { ...old.Input.tree.items };
                let cur: any = newItems[targetId];
                while (cur?.parentId) {
                    const parentId = cur.parentId;
                    if (newItems[parentId]) {
                        newItems[parentId] = { ...newItems[parentId], expanded: true };
                    }
                    cur = newItems[parentId];
                }
                if (newItems[targetId]?.childrens?.length > 0) {
                    newItems[targetId] = { ...newItems[targetId], expanded: true };
                }
                return {
                    ...old,
                    Input: { ...old.Input, tree: { ...old.Input.tree, items: newItems } },
                };
            }
        );

        setTimeout(() => { const el = document.querySelector(`[data-item-id="${targetId}"]`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300);
        window.history.replaceState({}, '');
    }, [location.state?.targetInputModelDataId, sections]);

    /**
     * Navigate to a sensitivity-specific error.
     * Uses the same section click flow that works when users manually click sections,
     * then polls the tree cache until the target item appears (loaded with sensitivity overlay).
     */
    const navigateToSensitivityError = useCallback(async (error: any) => {
        const itemId = error.itemId?.toString();
        if (!itemId) return;
        const path = error._path || [];

        const targetSection = resolveErrorSection(path, sections);
        if (!targetSection) return;

        setCurrentSection(targetSection);
        setViewMode('section');

        // Fetch sensitivity data for overlay
        const { getSensitivity } = await import('../../../shared/api/scenarios');
        let sensitivityData: any = null;
        if (currentSensitivity && currentSensitivity !== 'null') {
            try { sensitivityData = await getSensitivity(currentSensitivity.id); } catch (err) { /* ignore */ }
        }
        const sensitivityModelDatas = sensitivityData?.model_datas || [];

        // Helper: apply sensitivity overlay on a mapped item
        const applySensitivityOverlayOnItem = (item: any) => {
            item.sensitivityModelData = null;
            const sObj = sensitivityModelDatas.find((s: any) => s.original_model_data_id == item.id);
            if (sObj) item.sensitivityModelData = sObj;
            return item;
        };

        // Load section data (same as scenario navigateToError, but apply sensitivity overlay)
        const loadedItems: Record<string, any> = {};
        try {
            const response = await apiService.getSectionData(currentScenario.id, targetSection.config.id, false);
            if (response?.ok) {
                const responseData = await response.json();
                for (const md of (responseData['input'] || [])) {
                    loadedItems[md.id.toString()] = applySensitivityOverlayOnItem(mapModelData(md));
                }
            }
        } catch (err) { return; }

        // Walk ancestors, fetch children at each level (same loop as scenario navigateToError)
        for (const ancestor of path) {
            const ancestorId = ancestor.id.toString();
            const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
            const ancestorItem = loadedItems[ancestorId] || currentState?.Input?.tree?.items?.[ancestorId];
            if (!ancestorItem) continue;
            useEditorStore.getState().setNodeExpanded('Input' + ancestorId, true);
            if (loadedItems[ancestorId]) loadedItems[ancestorId].expanded = true;
            if (ancestorItem.childrens?.length > 0) {
                const firstChildId = ancestorItem.childrens[0];
                const currentItems = currentState?.Input?.tree?.items ?? {};
                if (!loadedItems[firstChildId] && !currentItems[firstChildId]) {
                    try {
                        const resp = await apiService.getModelDataChildren(parseInt(ancestorId));
                        if (resp?.ok) {
                            for (const child of await resp.json()) {
                                loadedItems[child.id.toString()] = applySensitivityOverlayOnItem(mapModelData(child));
                            }
                        }
                    } catch (err) { /* ignore */ }
                }
            }
        }

        // Merge all loaded items + expand ancestors in tree cache
        queryClient.setQueryData<EditorState>(
            [SCENARIO_TREE_KEY, scenarioId],
            (old) => {
                if (!old) return old;
                const newInputItems = { ...old.Input.tree.items, ...loadedItems };
                for (const ancestor of path) {
                    const id = ancestor.id.toString();
                    if (newInputItems[id]) newInputItems[id] = { ...newInputItems[id], expanded: true };
                }
                const realRootId = Object.keys(loadedItems).find(id => loadedItems[id]?.type === 'input-root') || old.Input.tree.rootId;
                return {
                    ...old,
                    Input: {
                        ...old.Input,
                        tree: { ...old.Input.tree, items: newInputItems, rootId: realRootId },
                    },
                };
            }
        );

        setTimeout(() => {
            const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
            const targetItem = currentState?.Input?.tree?.items?.[itemId] || loadedItems[itemId];
            if (targetItem) setModelDataSelected(targetItem);
            setTimeout(() => {
                const el = document.querySelector(`[data-item-id="${itemId}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200);
        }, 100);
    }, [queryClient, scenarioId, sections, currentScenario, currentSensitivity, setCurrentSection, setViewMode, setModelDataSelected]);

    return { navigateToError, navigateToSensitivityError };
}
